import { useNavigate } from 'react-router-dom';
import { ExternalLink, Pencil, Plus, Star } from 'lucide-react';

import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { PublicPagePanel } from '@/components/layout/PublicPagePanel';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { EmptyState, ErrorState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { useCurrentUser } from '@/features/auth/api';
import { can } from '@/lib/permissions';
import { cx } from '@/lib/cx';
import buttonStyles from '@/components/ui/Button.module.css';

import { useWebsiteSection } from './websiteApi';
import type { WebsiteSectionConfig } from './sections';
import styles from './WebsiteContent.module.css';

function publicUrl(section: WebsiteSectionConfig, slug?: string) {
  const { kind } = section;
  let path: string = section.publicPath;
  if (slug && kind === 'blog') path = `/post/${encodeURIComponent(slug)}/`;
  if (slug && kind === 'press') path += `${encodeURIComponent(slug)}/`;
  if (slug && kind === 'event') path += `#event-${encodeURIComponent(slug)}`;
  return `https://www.flyst.co.in${path}`;
}

function PublicLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      className={cx(buttonStyles.btn, buttonStyles.ghost, buttonStyles.sm, styles.link)}
      href={href}
      target="_blank"
      rel="noreferrer"
    >
      {label} <ExternalLink size={14} aria-hidden="true" />
      <span className="visually-hidden"> (opens in a new tab)</span>
    </a>
  );
}

export function WebsiteContentPage({ section }: { section: WebsiteSectionConfig }) {
  const { kind } = section;
  const { data: user } = useCurrentUser();
  const query = useWebsiteSection(kind);
  const navigate = useNavigate();
  const canEdit = can(user, 'website', 'edit');

  return (
    <Page>
      <PageHeader
        title={section.title}
        description="Manage content for the public Flyst website."
        actions={
          can(user, 'website', 'create') ? (
            <Button
              icon={<Plus size={16} />}
              onClick={() => {
                void navigate(`${section.path}/new`);
              }}
            >
              New {section.item}
            </Button>
          ) : null
        }
      />
      <div className={styles.sectionPanels}>
        <PublicPagePanel
          title={`Public ${section.title} page`}
          description="Published content appears on this page automatically."
          href={publicUrl(section)}
          label={`View ${section.title} page`}
        />
        <Panel title={section.title}>
          {query.isPending ? (
            <Spinner label="Loading website content" />
          ) : query.isError ? (
            <ErrorState
              message="The website content did not load."
              onRetry={() => {
                void query.refetch();
              }}
            />
          ) : query.data.items.length === 0 ? (
            <EmptyState
              title={`No ${section.title.toLowerCase()} yet`}
              body={`Create a ${section.item} and publish it when ready.`}
            />
          ) : (
            <ul className={styles.entries} role="list">
              {query.data.items.map((entry) => {
                const publisher = kind === 'press' ? entry.sources?.[0] : undefined;
                const href =
                  publisher && !entry.body?.length
                    ? publisher.url
                    : publicUrl(section, entry.slug);
                return (
                  <li key={entry.id} className={styles.entry}>
                    <div className={styles.entryInfo}>
                      <h3>{publisher?.name ?? entry.title}</h3>
                      {publisher ? <p className={styles.entrySummary}>{entry.title}</p> : null}
                      <div className={styles.badges}>
                        <Chip tone={entry.state === 'published' ? 'approved' : 'neutral'}>
                          {entry.state === 'published' ? 'Published' : 'Draft'}
                        </Chip>
                        {query.data.featured_id === entry.id ? (
                          <Chip tone="neutral">
                            <Star size={12} aria-hidden="true" /> Featured
                          </Chip>
                        ) : null}
                        {entry.category || entry.event_format ? (
                          <span className={styles.help}>
                            {entry.category || entry.event_format}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className={styles.actions}>
                      {entry.state === 'published' ? (
                        <PublicLink href={href} label="View" />
                      ) : null}
                      {canEdit ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<Pencil size={15} />}
                          onClick={() => {
                            void navigate(`${section.path}/${entry.id}/edit`);
                          }}
                        >
                          Edit
                        </Button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>
    </Page>
  );
}
